import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["placement", "timeout", "gutter"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { useControlledState } from 'ariakit-utils/hooks';
import { usePopoverState } from '../popover/popover-state.js';
import { c as createGlobalTooltipState } from '../__utils-30a60887.js';
var globalState = createGlobalTooltipState();
/**
 * Provides state for the `Tooltip` components.
 * @example
 * ```jsx
 * const tooltip = useTooltipState();
 * <TooltipAnchor state={tooltip}>Anchor</TooltipAnchor>
 * <Tooltip state={tooltip}>Tooltip</Tooltip>
 * ```
 */

function useTooltipState(_temp) {
  var _props$defaultOpen, _props$defaultOpen2, _props$open;

  var _ref = _temp === void 0 ? {} : _temp,
      _ref$placement = _ref.placement,
      placement = _ref$placement === void 0 ? "top" : _ref$placement,
      _ref$timeout = _ref.timeout,
      timeout = _ref$timeout === void 0 ? 0 : _ref$timeout,
      _ref$gutter = _ref.gutter,
      gutter = _ref$gutter === void 0 ? 8 : _ref$gutter,
      props = _objectWithoutProperties(_ref, _excluded);

  var ref = useRef();
  var showTimeout = useRef();
  var hideTimeout = useRef();
  var clearTimeouts = useCallback(function () {
    window.clearTimeout(showTimeout.current);
    window.clearTimeout(hideTimeout.current);
  }, []);

  var _useState = useState((_props$defaultOpen = props.defaultOpen) != null ? _props$defaultOpen : false),
      _useState2 = _slicedToArray(_useState, 2),
      _open = _useState2[0],
      __setOpen = _useState2[1];

  var _setOpen = function _setOpen(nextOpen) {
    props.setOpen == null ? void 0 : props.setOpen(nextOpen);

    if (props.open === undefined) {
      __setOpen(nextOpen);
    }
  };

  var _useControlledState = useControlledState((_props$defaultOpen2 = props.defaultOpen) != null ? _props$defaultOpen2 : false, (_props$open = props.open) != null ? _props$open : _open, function (nextOpen) {
    clearTimeouts();

    if (nextOpen) {
      if (!timeout || globalState.activeRef) {
        // If there's no timeout or an open tooltip already, we can show this
        // immediately.
        globalState.show(ref);
      } else {
        // There may be a reference with focus whose tooltip is still not
        // open. In this case, we want to update it before it gets shown.
        globalState.show(null); // Wait for the timeout to show the tooltip.

        showTimeout.current = window.setTimeout(function () {
          globalState.show(ref);

          _setOpen(nextOpen);
        }, timeout);
        return;
      }
    } else {
      // Let's give some time so people can move from a reference to
      // another and still show tooltips immediately.
      hideTimeout.current = window.setTimeout(function () {
        globalState.hide(ref);
      }, timeout);
    }

    _setOpen(nextOpen);
  }),
      _useControlledState2 = _slicedToArray(_useControlledState, 2),
      open = _useControlledState2[0],
      setOpen = _useControlledState2[1];

  var popover = usePopoverState(_objectSpread(_objectSpread({
    placement: placement,
    gutter: gutter
  }, props), {}, {
    open: open,
    setOpen: setOpen
  }));
  useEffect(function () {
    return globalState.subscribe(function (activeRef) {
      if (activeRef !== ref) {
        clearTimeouts();

        if (popover.open) {
          // Make sure there will be only one tooltip open
          popover.hide();
        }
      }
    });
  }, [clearTimeouts, popover.open, popover.hide]);
  useEffect(function () {
    return function () {
      clearTimeouts();
      globalState.hide(ref);
    };
  }, [clearTimeouts]);
  var state = useMemo(function () {
    return _objectSpread(_objectSpread({}, popover), {}, {
      timeout: timeout
    });
  }, [popover, timeout]);
  return state;
}

export { useTooltipState };